/**
 * Tags store for managing tags data
 * 
 * This store handles caching and syncing of tags for games,
 * using the GenericStore pattern for persistent caching.
 */

import { storeRegistry } from "@/rerun-manager/GenericStore";
import { apiService } from "@/lib/api-functions/api-service";

// Tag interface matching the API response
export interface TagItem {
  id: string;
  game_id: string;
  name: string;
  group_name?: string; // Optional group for organizing tags
  description?: string;
  color?: string; // Optional color for tag display
  created_at: string;
  updated_at: string;
}

/**
 * Store for tags, using GenericStore
 */
export const tagsStore = storeRegistry.getStore<TagItem>({
  key: 'tags',
  ttlMs: 1000 * 60 * 10, // 10 minutes cache
});

/**
 * Fetch tags for a specific game from the API and sync the store
 * @param gameId - The game ID to fetch tags for
 * @param toast - Optional toast function for error reporting
 */
export async function fetchTags(
  gameId: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<TagItem[]> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Fetch data from API
    const result = await apiService.fetchTags(gameId);
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to fetch tags');
    }

    const tags = result.data;

    // Sync the store with the fetched data
    await tagsStore.syncFromRemote(async () => tags);

    return tags;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch tags';
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Create a new tag via API and sync the store
 * @param tagData - The tag data to create
 * @param toast - Optional toast function for error reporting
 */
export async function createTag(
  tagData: {
    game_id: string;
    name: string;
    group?: string;
    description?: string;
    color?: string;
  },
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<TagItem> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Create data via API - map group to group_name for API
    const apiData = {
      ...tagData,
      group: tagData.group // The API expects 'group' but stores it as 'group_name'
    };
    const result = await apiService.createTag(apiData);
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to create tag');
    }

    const newTag = result.data;

    // Add the new tag to the store
    const currentData = tagsStore.getData();
    const updatedData = [...currentData, newTag];
    tagsStore.setData(updatedData);

    return newTag;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to create tag';
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Update an existing tag via API and sync the store
 * @param tagData - The tag data to update
 * @param toast - Optional toast function for error reporting
 */
export async function updateTag(
  tagData: {
    id: string;
    game_id: string;
    name: string;
    group?: string;
    description?: string;
    color?: string;
  },
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<TagItem> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Update data via API - map group to group_name for API
    const apiData = {
      ...tagData,
      group: tagData.group // The API expects 'group' but stores it as 'group_name'
    };
    const result = await apiService.updateTag(apiData);
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to update tag');
    }

    const updatedTag = result.data;

    // Update the tag in the store
    const currentData = tagsStore.getData();
    const updatedData = currentData.map(item => 
      item.id === tagData.id ? updatedTag : item
    );
    tagsStore.setData(updatedData);

    return updatedTag;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to update tag';
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Delete a tag via API and sync the store
 * @param id - The tag ID to delete
 * @param gameId - The game ID
 * @param toast - Optional toast function for error reporting
 */
export async function deleteTag(
  id: string,
  gameId: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<boolean> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Delete data via API
    const result = await apiService.deleteTag(id, gameId);
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to delete tag');
    }

    // Remove the tag from the store
    const currentData = tagsStore.getData();
    const filteredData = currentData.filter(item => item.id !== id);
    tagsStore.setData(filteredData);

    return true;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to delete tag';
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Get cached tags for a specific game
 * @param gameId - The game ID to get tags for
 */
export function getCachedTags(gameId: string): TagItem[] {
  const allTags = tagsStore.getData();
  return allTags.filter(item => item.game_id === gameId);
}

/**
 * Clear tags cache
 */
export async function clearTagsCache() {
  try {
    await tagsStore.clearCache();
    console.log('Tags cache cleared successfully');
  } catch (error) {
    console.error('Failed to clear tags cache:', error);
  }
} 
export function getTagById(id: string) {
  const tags = tagsStore.getData();
  return tags.find(item => item.id === id);
}

export function getTagByName(name: string) {
  const tags = tagsStore.getData();
  return tags.find(item => item.name === name);
}